import React, { useRef, useState } from 'react'
import { Box, Button, Chip, IconButton, Popover, TextField, Typography } from '@mui/material'
import { IconSeeker } from '@ui-seeker/core'

import { AppPillToggle } from '../@base'
import { dsColorNew } from '@palette'
import { FieldNote } from './FieldNote'


const CHIPS = 2
const CHIP_SX = {
	maxWidth: 120, minWidth: 0, flexShrink: 1, height: 24,
	'& .MuiChip-label': { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'block' },
}

/**
 * One field with kind pills (`company/ux/patterns.md` § 3; CU-869fmjzrh S2, generalised from "Cars", CU-869fmhmx8):
 * a band field whose menu picks the kind with pills and the values below, one kind at a time. Independent facets may
 * sit above the pills (`facets`). Under the field, what the filter means (`under`). The pure rules are in
 * `./kindPills.js`; each filter (Cars, Offices, Sites) is a thin wrapper that says its kinds and its lists.
 *
 * - `label` / `allText`: the field's label and what it says with nothing picked ("Cars" / "All cars");
 * - `on`, `labels`: whether something is picked and the chips that name it;
 * - `kinds`: the pills, `[{ value, label, note }]` (`kindPills`); `openTab()`: the pill the menu opens on;
 * - `renderKind(tab, close)`: the body of the menu for a pill; `noteFor(tab)`: the line under it (replace note);
 * - `onClear` / `clearLabel`: the footer's way back to everything.
 */
export default function KindPillsField({
	label, allText, on, labels = [], under, kinds, openTab, renderKind, noteFor = () => null, facets = null,
	onClear, clearLabel, pillsLabel, width = 380,
}) {
	const anchor = useRef(null)
	const [open, setOpen] = useState(false)
	const [tab, setTab] = useState(openTab())

	const show = () => {
		setTab(openTab())
		setOpen(true)
	}
	const close = () => setOpen(false)
	const note = noteFor(tab)

	return <Box>
		<TextField
			ref={anchor}
			size="small" fullWidth label={label} value={on ? '' : allText}
			onClick={show}
			onKeyDown={e => { if (['Enter', ' ', 'ArrowDown'].includes(e.key)) { e.preventDefault(); show() } }}
			inputProps={{ readOnly: true, role: 'button', 'aria-haspopup': 'dialog', 'aria-expanded': open, 'aria-label': on ? `${label}: ${labels.join(', ')}` : `${label}: ${allText}`, style: { cursor: 'pointer', width: on ? 0 : undefined, minWidth: on ? 0 : undefined } }}
			InputLabelProps={{ shrink: true, sx: on ? { color: `${dsColorNew.appBlue} !important` } : undefined }}
			InputProps={{
				readOnly: true,
				sx: { cursor: 'pointer', flexWrap: 'nowrap', overflow: 'hidden', ...(on && { '& fieldset': { borderColor: `${dsColorNew.appBlue} !important` } }) },
				// one line whatever the names: the chips shrink with an ellipsis (the theme's chip wraps its label), the
				// "+N" never shrinks, so long office names neither grow the field nor cover its label
				startAdornment: on ? <Box sx={{ display: 'flex', flexWrap: 'nowrap', alignItems: 'center', gap: 0.5, flex: '1 1 auto', minWidth: 0, overflow: 'hidden' }}>
					{labels.slice(0, CHIPS).map(l => <Chip key={l} size="small" label={l} title={l} sx={CHIP_SX}/>)}
					{labels.length > CHIPS && <Typography component="span" sx={{ fontSize: 12, color: dsColorNew.appBlue, fontWeight: 600, whiteSpace: 'nowrap', flexShrink: 0 }}>+{labels.length - CHIPS}</Typography>}
				</Box> : null,
				endAdornment: on
					? <IconButton size="small" aria-label={`Clear ${String(label).toLowerCase()}`} title="Clear" onClick={e => { e.stopPropagation(); onClear() }} sx={{ ml: 'auto', p: 0.5 }}>
						<IconSeeker icon="xmark" sx={{ fontSize: 12, color: dsColorNew.appBlue }}/>
					</IconButton>
					: <IconSeeker icon="chevron-down" sx={{ fontSize: 11, color: dsColorNew.textSubtle, ml: 'auto' }}/>,
			}}
		/>
		<FieldNote title={under}>{under}</FieldNote>

		<Popover open={open} anchorEl={anchor.current} onClose={close}
			anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }} transformOrigin={{ vertical: 'top', horizontal: 'left' }}
			PaperProps={{ sx: { mt: 0.5, p: 1.5, width, maxWidth: 'calc(100vw - 32px)', borderRadius: '12px' } }}>
			{facets}
			<AppPillToggle size="small" aria-label={pillsLabel} value={tab} onChange={setTab} sx={{ mb: 1.5 }}
				options={kinds.map(k => ({ value: k.value, label: k.note ? `${k.label} · ${k.note}` : k.label }))}/>

			{renderKind(tab, close)}

			{!!note && <Typography sx={{ fontSize: 12, color: dsColorNew.noteInfo, mt: 1 }}>{note}</Typography>}

			<Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1.5, pt: 1, borderTop: `1px solid ${dsColorNew.grayBorder}` }}>
				<Button variant="text" size="small" onClick={() => { close(); onClear() }} sx={{ textTransform: 'none' }}>{clearLabel}</Button>
				<Button variant="text" size="small" onClick={close} sx={{ textTransform: 'none', fontWeight: 600 }}>Done</Button>
			</Box>
		</Popover>
	</Box>
}
